import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Button } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';

/**
 * PermissionsEditor — security_group × entity_type permissions grid.
 *
 * The grid covers the FULL governed entity set (every row in
 * PERMISSION_ENTITIES), fetched from GET /api/permission-entities — the SINGLE
 * source of truth shared with the /api/me/permissions reflection. This replaces
 * the former hand-picked 12-entity grid that silently governed far fewer objects
 * than the backend actually enforces (the ~12-vs-36 drift).
 *
 *   - Rows are grouped by domain (Portfolio / Government / Tax / Billing / …).
 *   - super_admin implicitly holds every permission, so no per-entity tag is
 *     shown in this grid — it only edits what a non-super role is granted.
 *   - Existing rows updated via PUT /api/security-permissions/:id;
 *     new rows created via POST /api/security-permissions.
 */

const ACTIONS = ['can_view', 'can_add', 'can_edit', 'can_delete', 'can_process', 'can_export'] as const;
type Action = typeof ACTIONS[number];

interface EntityMeta {
  entity: string;
  label: string;
  group: string;
  restricted: boolean;
}

interface SecurityPermission {
  id: string;
  security_group_id: string;
  entity_type: string;
  can_view: boolean | number;
  can_add: boolean | number;
  can_edit: boolean | number;
  can_delete: boolean | number;
  can_process: boolean | number;
  can_export: boolean | number;
}

type PermRow = Record<Action, boolean>;
type PermGrid = Record<string, PermRow>;

interface PermissionsEditorProps {
  open: boolean;
  onClose: () => void;
  groupId: string | null;
  groupName: string;
}

function emptyRow(): PermRow {
  return ACTIONS.reduce((acc, a) => ({ ...acc, [a]: false }), {} as PermRow);
}

export function PermissionsEditor({ open, onClose, groupId, groupName }: PermissionsEditorProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [grid, setGrid] = useState<PermGrid>({});
  const [permIds, setPermIds] = useState<Record<string, string | null>>({});

  // The full governed entity catalog (label + group + restricted flag). Single
  // source of truth — the same PERMISSION_ENTITIES set the backend enforces.
  const catalogQuery = useQuery<{ entities: EntityMeta[] }>({
    queryKey: ['permission-entities'],
    queryFn: ({ signal }) => api.get<{ entities: EntityMeta[] }>('/api/permission-entities', signal),
    enabled: open,
    staleTime: 10 * 60_000,
  });
  const entities = catalogQuery.data?.entities ?? [];

  const query = useQuery<SecurityPermission[]>({
    queryKey: ['security-permissions'],
    queryFn: ({ signal }) => api.get<SecurityPermission[]>('/api/security-permissions', signal),
    enabled: open && !!groupId,
  });

  // Seed grid + permIds from fetched permissions + catalog when both resolve.
  useEffect(() => {
    if (!open || !groupId || !query.data || entities.length === 0) return;
    const groupPerms = query.data.filter(p => p.security_group_id === groupId);
    const nextGrid: PermGrid = {};
    const nextIds: Record<string, string | null> = {};
    for (const { entity } of entities) {
      const row = groupPerms.find(p => p.entity_type === entity);
      nextGrid[entity] = emptyRow();
      nextIds[entity] = row ? row.id : null;
      if (row) {
        for (const action of ACTIONS) nextGrid[entity][action] = !!row[action];
      }
    }
    setGrid(nextGrid);
    setPermIds(nextIds);
  }, [open, groupId, query.data, entities]);

  const mutation = useMutation<unknown, Error, void>({
    mutationFn: async () => {
      if (!groupId) return;
      // Sequential write: each entity_type → POST if no existing id, else PUT.
      for (const { entity } of entities) {
        const row = grid[entity];
        if (!row) continue;
        const id = permIds[entity];
        const body = {
          security_group_id: groupId,
          entity_type: entity,
          can_view: row.can_view,
          can_add: row.can_add,
          can_edit: row.can_edit,
          can_delete: row.can_delete,
          can_process: row.can_process,
          can_export: row.can_export,
        };
        if (id) {
          await api.put(`/api/security-permissions/${id}`, body);
        } else {
          await api.post('/api/security-permissions', body);
        }
      }
    },
    onSuccess: () => {
      toast({ variant: 'success', message: 'Permissions saved.' });
      queryClient.invalidateQueries({ queryKey: ['security-permissions'] });
      // The saved group may be the current user's own — refresh the reflection so
      // nav/route gating picks up the new grants immediately.
      queryClient.invalidateQueries({ queryKey: ['me-permissions'] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  function toggleCell(entity: string, action: Action) {
    setGrid(prev => ({ ...prev, [entity]: { ...(prev[entity] ?? emptyRow()), [action]: !(prev[entity]?.[action]) } }));
  }

  function setRow(entity: string, value: boolean) {
    setGrid(prev => ({
      ...prev,
      [entity]: ACTIONS.reduce((acc, a) => ({ ...acc, [a]: value }), {} as PermRow),
    }));
  }

  function setColumn(action: Action, value: boolean) {
    setGrid(prev => {
      const next = { ...prev };
      for (const { entity } of entities) {
        next[entity] = { ...(next[entity] ?? emptyRow()), [action]: value };
      }
      return next;
    });
  }

  // Group the catalog for section headers (catalog arrives group-sorted).
  const groups: { group: string; rows: EntityMeta[] }[] = [];
  for (const meta of entities) {
    const last = groups[groups.length - 1];
    if (last && last.group === meta.group) last.rows.push(meta);
    else groups.push({ group: meta.group, rows: [meta] });
  }

  const loading = catalogQuery.isLoading || query.isLoading;

  return (
    <Modal
      open={open}
      onClose={() => { if (!mutation.isPending) onClose(); }}
      title={`Permissions: ${groupName}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button variant="primary" loading={mutation.isPending} onClick={() => mutation.mutate()} disabled={loading}>
            Save permissions
          </Button>
        </>
      }
    >
      {loading ? (
        <p style={{ color: 'var(--color-ink-3)' }}>Loading permissions…</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
            <thead>
              <tr>
                <th scope="col" style={headerCellStyle}>Entity</th>
                {ACTIONS.map(a => (
                  <th scope="col" key={a} style={{ ...headerCellStyle, textAlign: 'center' }}>
                    <div>{actionLabel(a)}</div>
                    <ColumnToggle
                      action={a}
                      allOn={entities.length > 0 && entities.every(({ entity }) => grid[entity]?.[a])}
                      onToggle={(val) => setColumn(a, val)}
                    />
                  </th>
                ))}
                <th scope="col" style={{ ...headerCellStyle, textAlign: 'center' }}>All</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(({ group, rows }) => (
                <GroupBlock
                  key={group}
                  group={group}
                  rows={rows}
                  grid={grid}
                  onToggleCell={toggleCell}
                  onSetRow={setRow}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

function GroupBlock({
  group, rows, grid, onToggleCell, onSetRow,
}: {
  group: string;
  rows: EntityMeta[];
  grid: PermGrid;
  onToggleCell: (entity: string, action: Action) => void;
  onSetRow: (entity: string, value: boolean) => void;
}) {
  return (
    <>
      <tr>
        <td colSpan={ACTIONS.length + 2} style={{
          padding: 'var(--sp-3) var(--sp-3) var(--sp-1)',
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-ink-3)',
          background: 'var(--color-card)',
        }}>{group}</td>
      </tr>
      {rows.map(({ entity, label }) => {
        const row = grid[entity] ?? emptyRow();
        const allOn = ACTIONS.every(a => row[a]);
        return (
          <tr key={entity} style={{ borderTop: '1px solid var(--color-border)' }}>
            <td style={{ padding: 'var(--sp-2) var(--sp-3)', color: 'var(--color-ink)' }}>
              <span style={{ fontWeight: 'var(--fw-medium)' }}>{label}</span>
            </td>
            {ACTIONS.map(a => (
              <td key={a} style={{ textAlign: 'center', padding: 'var(--sp-2)' }}>
                <input
                  type="checkbox"
                  checked={row[a]}
                  onChange={() => onToggleCell(entity, a)}
                  aria-label={`${label} ${actionLabel(a)}`}
                  style={{ accentColor: 'var(--brand-primary)' }}
                />
              </td>
            ))}
            <td style={{ textAlign: 'center', padding: 'var(--sp-2)' }}>
              <input
                type="checkbox"
                checked={allOn}
                onChange={() => onSetRow(entity, !allOn)}
                aria-label={`${label} all actions`}
                style={{ accentColor: 'var(--brand-primary)' }}
              />
            </td>
          </tr>
        );
      })}
    </>
  );
}

function ColumnToggle({ action, allOn, onToggle }: { action: Action; allOn: boolean; onToggle: (val: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(!allOn)}
      title={`Toggle ${actionLabel(action)} for all entities`}
      style={{
        background: 'none',
        border: 'none',
        color: 'var(--color-muted)',
        fontSize: 'var(--fs-xs)',
        cursor: 'pointer',
        marginTop: 2,
        padding: 0,
      }}
    >
      {allOn ? 'clear' : 'all'}
    </button>
  );
}

const headerCellStyle: React.CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)',
  fontSize: 'var(--fs-xs)',
  fontWeight: 'var(--fw-semibold)',
  textTransform: 'uppercase',
  letterSpacing: 'var(--ls-wider)',
  color: 'var(--color-ink-3)',
  borderBottom: '1px solid var(--color-border)',
};

function actionLabel(a: Action): string {
  return a.replace('can_', '').replace(/\b\w/g, c => c.toUpperCase());
}
